import { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, Plus, Trash2, Sparkles, Lock, Zap, ArrowUpRight, Power, Film, Pencil, X, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import SafeThumb from "@/components/SafeThumb";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";

const EMPTY_FORM = {
  product_id: "",
  scope: "all", // 'all' | 'specific'
  media_id: "",
  match_type: "keyword", // 'keyword' | 'any'
  keywords: "",
  message_template: "",
  require_follow: false,
  public_reply_enabled: true,
  public_reply_text: "", // one variation per line (max 5); blank = built-in set
};

const MAX_PUBLIC_VARIATIONS = 5;
const MAX_PUBLIC_LEN = 150;
const parsePublicLines = (text) =>
  [...new Set(String(text || "").split("\n").map((l) => l.replace(/\s+/g, " ").trim()).filter(Boolean))];

// The 12 ready-made public replies. Keep this list identical to the DEFAULTS list in the
// "Build Public Reply" node, so "leave it blank" behaves exactly like having all 12 selected.
const PUBLIC_REPLY_SUGGESTIONS = [
  "Hey {username}! 👋 Just sent you a DM with all the details. Check your inbox 📩",
  "{username} you're all set! 🎉 Check your DMs for the link.",
  "Done! ✅ {username}, I've slid into your DMs 😉 Take a look!",
  "Thanks for commenting, {username}! 🙌 Your DM is on its way, check your messages.",
  "{username} check your DMs 📩 Everything you asked for is there!",
  "Hi {username}! 😍 Sent you a message about {product_name}. Peek inside your DMs!",
  "On it, {username}! 🚀 The details are waiting in your DMs.",
  "Yay, {username}! 🎁 Your DM has landed. Go check it out 💌",
  "Sent! ✨ {username}, look out for our message in your DMs.",
  "{username}, we've just DM'd you about {product_name} 👀 Check your inbox (and Message Requests, just in case)!",
  "Hey {username} 💬 Check your DMs, we've sent you what you asked for!",
  "Got it, {username}! 🙌 The details are in your DMs now.",
];

const newestFirst = (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);
const latestRule = (rules) => [...rules].sort(newestFirst)[0] || null;

// A new rule starts with the settings from the merchant's most recent rule
// (follow gate, private reply wording, public reply on/off + wording).
// Product, trigger and keywords are never copied.
const carryOverSettings = (rules) => {
  const r = latestRule(rules);
  if (!r) return {};
  return {
    require_follow: !!r.require_follow,
    message_template: r.message_template || "",
    public_reply_enabled: r.public_reply_enabled !== false,
    public_reply_text: (r.public_reply_templates || []).join("\n"),
  };
};

// Curated starting points for the reply message, shown as tappable chips.
// These use the same {product_name}/{product_price}/{currency} placeholders
// the bot resolves at send-time, so they stay generic across every rule.
const SAMPLE_TEMPLATES = [
  "Hey! 👋 Here's the {product_name} you asked about — {currency}{product_price}. I've sent it to your DMs so you can pick a size/quantity and check out 🛍️",
  "Yes, it's available! 😍 {product_name} is {currency}{product_price}. Check your DMs — I've sent you the checkout link!",
  "Thanks for your interest! I've DM'd you the {product_name} ({currency}{product_price}) so you can complete your order right there 🙌",
  "On it! 🚀 Sent you a DM with the {product_name} — {currency}{product_price}. Grab it before it's gone!",
];

export default function AutomationsPanel({ merchant }) {
  const enabled = merchant?.plan === "creator" || merchant?.plan === "starter" || merchant?.plan === "growth" || merchant?.plan === "pro";
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Tracks which rule row currently has a Pause/Activate request in flight,
  // so only that row's button shows a spinner + disables — not the whole list.
  const [togglingId, setTogglingId] = useState(null);

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  const [igMedia, setIgMedia] = useState([]);
  const [igMediaLoading, setIgMediaLoading] = useState(false);
  const [igMediaFetched, setIgMediaFetched] = useState(false);
  const [igMediaError, setIgMediaError] = useState("");
  const [manualMediaEntry, setManualMediaEntry] = useState(false);
  const [showPublicSuggestions, setShowPublicSuggestions] = useState(true);
  const prefilledRef = useRef(false);
  const rulesListRef = useRef(null);

  // Anchors the create/edit form so "Edit" can scroll here instead of to
  // the very top of the page.
  const formRef = useRef(null);

  // `silent` skips the full-panel "Loading…" state — used when refreshing
  // after a row-level action (toggle) where we don't want the whole rules
  // list to disappear behind a loading message, just the one button.
  const load = useCallback(
    (opts = {}) => {
      if (!enabled) {
        setLoading(false);
        return Promise.resolve();
      }
      if (!opts.silent) setLoading(true);
      return api
        .get("/merchant/automations")
        .then((r) => setRules(r.data.items || []))
        .catch(() => setRules([]))
        .finally(() => setLoading(false));
    },
    [enabled]
  );

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [load]);

  // Product picker needs the merchant's catalog so a rule can be tied to a
  // specific product_id — the workflow's "Get Matching Rules" query INNER
  // JOINs catalog_items on r.product_id, so a rule with no product can never
  // match a comment. GET /api/catalog returns { items, used, limit, plan }
  // where each item has `title` (not `name`) and an `active` flag that
  // means "counts toward your plan's SKU limit" — items beyond the limit
  // are locked, so we only offer active ones here.
  useEffect(() => {
    if (!enabled) {
      setCatalogLoading(false);
      return;
    }
    setCatalogLoading(true);
    api
      .get("/catalog")
      .then((r) => setCatalog((r.data.items || []).filter((p) => p.active)))
      .catch(() => setCatalog([]))
      .finally(() => setCatalogLoading(false));
  }, [enabled]);

  // Only hit the Instagram API once the merchant actually wants to pick a
  // specific post — no point fetching this on every panel load. Gated on
  // igMediaFetched (not igMedia.length) so a failed fetch doesn't retry in
  // a tight loop forever — it tries once, then waits for an explicit Retry.
  const fetchIgMedia = useCallback(() => {
    setIgMediaLoading(true);
    setIgMediaError("");
    api
      .get("/merchant/instagram-media")
      .then((r) => setIgMedia(r.data.items || []))
      .catch((e) => {
        setIgMediaError(
          e?.response?.data?.error === "instagram_not_connected"
            ? "Reconnect Instagram to pick from your posts."
            : "Couldn't load your recent posts — you can paste the media ID instead."
        );
      })
      .finally(() => {
        setIgMediaLoading(false);
        setIgMediaFetched(true);
      });
  }, []);

  useEffect(() => {
    if (!enabled || form.scope !== "specific" || igMediaFetched || igMediaLoading) return;
    fetchIgMedia();
  }, [enabled, form.scope, igMediaFetched, igMediaLoading, fetchIgMedia]);

  // Once the rules have loaded, pre-fill a NEW rule with the last rule's settings (only once,
  // and never while editing, so it can't overwrite anything the merchant is typing).
  useEffect(() => {
    if (prefilledRef.current || loading || editingId) return;
    prefilledRef.current = true;
    if (rules.length > 0) setForm((f) => ({ ...f, ...carryOverSettings(rules) }));
  }, [loading, rules, editingId]);

  if (!enabled) {
    return (
      <div className="rounded-[2rem] border border-ink/10 bg-white p-8 md:p-10" data-testid="automations-locked">
        <div className="flex items-start gap-4">
          <span className="grid h-14 w-14 flex-none place-items-center rounded-3xl bg-ink/5 text-ink/60">
            <Lock size={22} />
          </span>
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Comment DM Automation</p>
            <h3 className="mt-1 font-display text-2xl font-black tracking-tight">
              Unlock automations from Creator
            </h3>
            <p className="mt-2 max-w-xl text-sm text-ink/60">
              Turn any Instagram comment into a private checkout. Pick a product, set the
              keywords, we send the DM — instantly. Available on Creator, Starter, Growth & Pro.
            </p>
            <a
              href="/plans"
              data-testid="automations-upgrade-cta"
              className="cta-glow mt-5 inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2.5 text-sm font-semibold text-white"
            >
              Upgrade to Creator <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </div>
    );
  }

  const resetFormWith = (carry) => {
    setForm({ ...EMPTY_FORM, ...carry });
    setEditingId(null);
    setManualMediaEntry(false);
  };
  const resetForm = () => resetFormWith(carryOverSettings(rules));

  const startEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      product_id: rule.product_id != null ? String(rule.product_id) : "",
      scope: rule.scope === "specific" ? "specific" : "all",
      media_id: rule.media_id || "",
      match_type: rule.match_type === "any" ? "any" : "keyword",
      keywords: (rule.keywords || []).join(", "),
      message_template: rule.message_template || "",
      require_follow: !!rule.require_follow,
      public_reply_enabled: rule.public_reply_enabled !== false,
      public_reply_text: (rule.public_reply_templates || []).join("\n"),
    });
    // If this rule already points at a post, prefer the manual field showing
    // the existing ID rather than waiting on a recent-posts fetch to match it.
    setManualMediaEntry(rule.scope === "specific");
    // Scroll to the edit form itself, not to the top of the whole page —
    // on longer rule lists "Edit" could be clicked far below the fold.
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_id) return toast.error("Pick a product for this rule.");
    if (form.scope === "specific" && !form.media_id.trim()) {
      return toast.error("Select the post or enter its media ID, or switch scope to \"All posts\".");
    }
    const kws = form.keywords.split(",").map((s) => s.trim()).filter(Boolean);
    if (form.match_type === "keyword" && kws.length === 0) {
      return toast.error("Add at least one keyword, or switch to \"Any comment\".");
    }
    const publicLines = parsePublicLines(form.public_reply_text);
    if (form.public_reply_enabled) {
      if (publicLines.length > MAX_PUBLIC_VARIATIONS) {
        return toast.error(`Add at most ${MAX_PUBLIC_VARIATIONS} public reply variations.`);
      }
      if (publicLines.some((l) => l.length > MAX_PUBLIC_LEN)) {
        return toast.error(`Each public reply must be ${MAX_PUBLIC_LEN} characters or fewer.`);
      }
      if (publicLines.some((l) => /https?:\/\/|www\./i.test(l))) {
        return toast.error("Public replies can't contain links. Links go in the private DM.");
      }
    }
    // Reply message is genuinely optional — an empty value falls back to our
    // default wording server-side, so it must NOT be blocked here.

    setCreating(true);
    try {
      if (editingId) {
        await api.patch("/merchant/automations", {
          id: editingId,
          product_id: form.product_id,
          scope: form.scope,
          media_id: form.scope === "specific" ? form.media_id.trim() : null,
          match_type: form.match_type,
          keywords: form.match_type === "keyword" ? kws : [],
          message_template: form.message_template.trim(),
          require_follow: !!form.require_follow,
          public_reply_enabled: !!form.public_reply_enabled,
          public_reply_templates: publicLines,
        });
        toast.success("Automation rule updated.");
      } else {
        await api.post("/merchant/automations", {
          product_id: form.product_id,
          scope: form.scope,
          media_id: form.scope === "specific" ? form.media_id.trim() : null,
          match_type: form.match_type,
          keywords: form.match_type === "keyword" ? kws : [],
          message_template: form.message_template.trim(),
          require_follow: !!form.require_follow,
          public_reply_enabled: !!form.public_reply_enabled,
          public_reply_templates: publicLines,
          active: true,
        });
        toast.success("Automation rule created.");
      }
      // The rule just saved becomes the newest one, so the next new rule starts from its settings.
      const savedSettings = {
        require_follow: !!form.require_follow,
        message_template: form.message_template.trim(),
        public_reply_enabled: !!form.public_reply_enabled,
        public_reply_text: publicLines.join("\n"),
      };
      const editedTheLatest = editingId && latestRule(rules)?.id === editingId;
      resetFormWith(!editingId || editedTheLatest ? savedSettings : carryOverSettings(rules));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (editingId ? "Could not update rule." : "Could not create rule."));
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (rule) => {
    if (togglingId) return; // one toggle in flight at a time is plenty
    setTogglingId(rule.id);
    try {
      // PATCH /merchant/automations takes the id in the body, not a path
      // segment — matches your PUT /catalog/:id convention.
      await api.patch("/merchant/automations", { id: rule.id, active: !rule.active });
      await load({ silent: true }); // silent: keep the list visible while refreshing
    } catch {
      toast.error("Could not update rule.");
    } finally {
      setTogglingId(null);
    }
  };

  const remove = (rule) => setDeleteTarget(rule);

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await api.post("/merchant/automations/delete", { id: deleteTarget.id });
      toast.success("Rule deleted.");
      if (editingId === deleteTarget.id) resetForm();
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Could not delete rule.");
    } finally {
      setDeleting(false);
    }
  };

  const selectedProduct = catalog.find((p) => String(p.id) === String(form.product_id));

  // Public reply picker: tap a ready-made reply to add it, tap again to remove it.
  const selectedPublic = parsePublicLines(form.public_reply_text);
  const toggleSuggestion = (text) => {
    if (selectedPublic.includes(text)) {
      setForm({ ...form, public_reply_text: selectedPublic.filter((l) => l !== text).join("\n") });
    } else if (selectedPublic.length >= MAX_PUBLIC_VARIATIONS) {
      toast.error(`You can keep up to ${MAX_PUBLIC_VARIATIONS} replies. Tap a selected one to remove it first.`);
    } else {
      setForm({ ...form, public_reply_text: [...selectedPublic, text].join("\n") });
    }
  };

  // Offer the last few distinct wordings the merchant has already used, so a
  // new rule can reuse one instead of retyping — most recent first.
  const previousTemplates = [...rules]
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    .map((r) => (r.message_template || "").trim())
    .filter(Boolean)
    .filter((t, i, arr) => arr.indexOf(t) === i)
    .slice(0, 3);

  return (
    <div className="space-y-6" data-testid="automations-panel">
      {/* Header */}
      <div className="overflow-hidden rounded-[2rem] bg-sunset-gradient p-8 text-white shadow-lg md:p-10">
        <div className="flex items-start gap-3">
          <Sparkles size={22} />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/85">
              Comment DM Automation · {merchant?.plan_meta?.name} plan
            </p>
            <h3 className="mt-1 font-display text-2xl font-black tracking-tight md:text-3xl">
              Turn Instagram comments into paid orders.
            </h3>
            <p className="mt-2 max-w-xl text-white/85">
              When someone comments on your post or Reel, we&apos;ll send them a private DM with
              your product&apos;s photo, price, and a &quot;Select this&quot; button — automatically.
            </p>
          </div>
        </div>
      </div>

      {/* Jump link: on a phone the rules list sits below the long form */}
      {rules.length > 0 && (
        <button
          type="button"
          onClick={() => rulesListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
          data-testid="jump-to-rules"
          className="flex w-full items-center justify-between rounded-2xl border border-ink/10 bg-white px-5 py-3 text-sm font-semibold md:hidden"
        >
          <span className="inline-flex items-center gap-2">
            <Zap size={14} className="text-hotpink" /> View your {rules.length} existing rule{rules.length === 1 ? "" : "s"}
          </span>
          <ChevronDown size={16} className="text-ink/50" />
        </button>
      )}

      {/* Create / edit rule */}
      <form
        ref={formRef}
        onSubmit={submit}
        className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8"
        data-testid="automation-create-form"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {editingId ? <Pencil size={16} className="text-hotpink" /> : <Plus size={16} className="text-hotpink" />}
            <p className="font-display text-lg font-black tracking-tight">
              {editingId ? "Edit automation rule" : "Create automation rule"}
            </p>
          </div>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              data-testid="rule-cancel-edit"
              className="inline-flex items-center gap-1 rounded-full border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-ink/5"
            >
              <X size={12} /> Cancel
            </button>
          )}
        </div>

        {!editingId && rules.length > 0 && (
          <p className="mt-2 text-xs text-ink/50" data-testid="rule-prefill-hint">
            Follow, reply and public reply settings are copied from your last rule. Change anything you like.
          </p>
        )}

        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 [&>*]:min-w-0">
          {/* Product */}
          <label className="text-sm md:col-span-2">
            <span className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-ink/60">Product</span>
            <select
              value={form.product_id}
              onChange={(e) => setForm({ ...form, product_id: e.target.value })}
              data-testid="rule-product"
              disabled={catalogLoading}
              className="w-full rounded-2xl border border-ink/15 bg-white px-3 py-2 focus:border-hotpink focus:outline-none disabled:opacity-60"
            >
              <option value="">
                {catalogLoading ? "Loading catalog…" : "Select a product…"}
              </option>
              {catalog.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title} — {formatMoney(p.price, merchant?.currency)}{p.sold_out ? " (Sold out)" : ""}
                </option>
              ))}
            </select>
            {!catalogLoading && catalog.length === 0 && (
              <p className="mt-1.5 text-xs text-ink/50">
                No products yet — add one in your Catalog first.
              </p>
            )}
          </label>

          {/* Scope */}
          <div className="text-sm">
            <span className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-ink/60">Trigger on</span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, scope: "all" })}
                data-testid="rule-scope-all"
                className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                  form.scope === "all" ? "bg-ink text-cream" : "border border-ink/15 bg-white"
                }`}
              >
                All posts &amp; Reels
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, scope: "specific" })}
                data-testid="rule-scope-specific"
                className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                  form.scope === "specific" ? "bg-ink text-cream" : "border border-ink/15 bg-white"
                }`}
              >
                One specific post
              </button>
            </div>
          </div>

          {/* Match type */}
          <div className="text-sm">
            <span className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-ink/60">Match</span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, match_type: "keyword" })}
                data-testid="rule-match-keyword"
                className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                  form.match_type === "keyword" ? "bg-ink text-cream" : "border border-ink/15 bg-white"
                }`}
              >
                Specific keywords
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, match_type: "any" })}
                data-testid="rule-match-any"
                className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                  form.match_type === "any" ? "bg-ink text-cream" : "border border-ink/15 bg-white"
                }`}
              >
                Any comment
              </button>
            </div>
          </div>

          {form.scope === "specific" && (
            <div className="text-sm md:col-span-2">
              <span className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-ink/60">
                Which post or Reel?
              </span>

              {igMediaLoading && (
                <p className="flex items-center gap-1.5 text-xs text-ink/50">
                  <Loader2 size={12} className="animate-spin" /> Loading your recent posts…
                </p>
              )}

              {!igMediaLoading && !manualMediaEntry && igMedia.length > 0 && (
                <>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {igMedia.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setForm({ ...form, media_id: item.id })}
                        title={item.caption || undefined}
                        data-testid={`ig-media-${item.id}`}
                        className={`relative h-16 w-16 flex-none overflow-hidden rounded-xl border-2 ${
                          form.media_id === item.id ? "border-hotpink" : "border-transparent"
                        }`}
                      >
                        {item.thumbnail ? (
                          <SafeThumb src={item.thumbnail} />
                        ) : (
                          <span className="grid h-full w-full place-items-center bg-ink/5 text-ink/30">
                            <Film size={16} />
                          </span>
                        )}
                        {item.is_reel && (
                          <span className="absolute bottom-1 right-1 grid h-4 w-4 place-items-center rounded-full bg-ink/70 text-white">
                            <Film size={10} />
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setManualMediaEntry(true)}
                    className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-hotpink hover:underline"
                  >
                    <Pencil size={11} /> Can't find it? Paste the media ID instead
                  </button>
                </>
              )}

              {!igMediaLoading && (manualMediaEntry || igMedia.length === 0) && (
                <>
                  {igMediaError && (
                    <p className="mb-1.5 text-xs text-hotpink">
                      {igMediaError}{" "}
                      <button
                        type="button"
                        onClick={() => setIgMediaFetched(false)}
                        className="font-semibold underline"
                      >
                        Retry
                      </button>
                    </p>
                  )}
                  <input
                    value={form.media_id}
                    onChange={(e) => setForm({ ...form, media_id: e.target.value })}
                    placeholder="e.g. 17895695668004550"
                    data-testid="rule-media-id"
                    className="w-full rounded-2xl border border-ink/15 bg-white px-3 py-2 focus:border-hotpink focus:outline-none"
                  />
                  <p className="mt-1 text-xs text-ink/40">
                    The numeric media ID of the exact post this rule should fire on.
                  </p>
                  {!igMediaError && igMedia.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setManualMediaEntry(false)}
                      className="mt-1.5 text-xs font-semibold text-hotpink hover:underline"
                    >
                      Pick from your recent posts instead
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {form.match_type === "keyword" && (
            <label className="text-sm md:col-span-2">
              <span className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-ink/60">
                Keywords <span className="text-ink/40">(comma-separated)</span>
              </span>
              <input
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                placeholder="tote, bag, buy"
                data-testid="rule-keywords"
                className="w-full rounded-2xl border border-ink/15 px-3 py-2 focus:border-hotpink focus:outline-none"
              />
            </label>
          )}

          <label
            className="flex cursor-pointer items-start gap-3 rounded-2xl border border-ink/10 bg-ink/[0.02] p-4 text-sm md:col-span-2"
            data-testid="rule-require-follow"
          >
            <input
              type="checkbox"
              checked={!!form.require_follow}
              onChange={(e) => setForm({ ...form, require_follow: e.target.checked })}
              className="mt-0.5 h-4 w-4 accent-hotpink"
            />
            <span>
              <span className="block font-semibold text-ink">Require follow before sending</span>
              <span className="mt-0.5 block text-xs text-ink/60">
                Commenters get a &quot;Follow us to unlock&quot; card with Visit profile and I&apos;m following buttons.
                The product card is sent only after they follow and tap &quot;I&apos;m following&quot;. The private
                reply message below isn&apos;t used when this is on.
              </span>
            </span>
          </label>

          <div className="text-sm md:col-span-2">
            <label
              htmlFor="rule-template"
              className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-ink/60"
            >
              Private reply message
            </label>

            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-ink/40">Suggested:</span>
              {SAMPLE_TEMPLATES.map((t, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setForm({ ...form, message_template: t })}
                  title={t}
                  data-testid={`sample-template-${i}`}
                  className="max-w-[220px] truncate rounded-full border border-ink/15 bg-white px-3 py-1 text-xs font-medium text-ink/70 hover:border-hotpink hover:text-hotpink"
                >
                  {t}
                </button>
              ))}
            </div>

            {previousTemplates.length > 0 && (
              <div className="mb-2 flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-ink/40">Reuse:</span>
                {previousTemplates.map((t, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setForm({ ...form, message_template: t })}
                    title={t}
                    className="max-w-[220px] truncate rounded-full border border-ink/15 bg-white px-3 py-1 text-xs font-medium text-ink/70 hover:border-hotpink hover:text-hotpink"
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}

            <textarea
              id="rule-template"
              value={form.message_template}
              onChange={(e) => setForm({ ...form, message_template: e.target.value })}
              placeholder="Hey! 👋 Here's the {product_name} you asked about — {currency} {product_price}. I've sent it to your DMs so you can pick a size/quantity and check out 🛍️"
              data-testid="rule-template"
              rows={3}
              className="w-full rounded-2xl border border-ink/15 px-3 py-2 focus:border-hotpink focus:outline-none"
            />
            <p className="mt-1 text-xs text-ink/40">
              Optional — leave blank to use our default wording. Available placeholders:{" "}
              <code className="rounded bg-ink/5 px-1 py-0.5">{"{product_name}"}</code>{" "}
              <code className="rounded bg-ink/5 px-1 py-0.5">{"{product_price}"}</code>{" "}
              <code className="rounded bg-ink/5 px-1 py-0.5">{"{currency}"}</code>. This is the
              comment reply only — the product photo, price, and checkout button go out
              separately as a DM.
            </p>
          </div>

          <div
            className="rounded-2xl border border-ink/10 bg-ink/[0.02] p-4 text-sm md:col-span-2"
            data-testid="rule-public-reply"
          >
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={!!form.public_reply_enabled}
                onChange={(e) => setForm({ ...form, public_reply_enabled: e.target.checked })}
                className="mt-0.5 h-4 w-4 accent-hotpink"
                data-testid="rule-public-reply-toggle"
              />
              <span>
                <span className="block font-semibold text-ink">Also reply publicly under the comment</span>
                <span className="mt-0.5 block text-xs text-ink/60">
                  After the DM goes out, we post a short reply under their comment (for example &quot;@name check your DMs 📩&quot;)
                  so you and everyone else can see the comment was answered. Turn this off to send the DM only.
                </span>
              </span>
            </label>

            {form.public_reply_enabled && (
              <div className="mt-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-ink/60">
                    Your public replies{" "}
                    <span className="text-ink/40">
                      ({selectedPublic.length} of {MAX_PUBLIC_VARIATIONS} selected)
                    </span>
                  </span>
                  {selectedPublic.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, public_reply_text: "" })}
                      className="text-xs font-semibold text-ink/50 hover:text-hotpink"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => setShowPublicSuggestions((v) => !v)}
                  data-testid="rule-public-suggestions-toggle"
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-hotpink hover:underline"
                >
                  <ChevronDown size={13} className={showPublicSuggestions ? "rotate-180 transition-transform" : "transition-transform"} />
                  {showPublicSuggestions ? "Hide" : "Browse"} {PUBLIC_REPLY_SUGGESTIONS.length} ready-made replies
                </button>

                {showPublicSuggestions && (
                  <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2" data-testid="rule-public-suggestions">
                    {PUBLIC_REPLY_SUGGESTIONS.map((t, i) => {
                      const picked = selectedPublic.includes(t);
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => toggleSuggestion(t)}
                          aria-pressed={picked}
                          data-testid={`public-suggestion-${i}`}
                          className={`flex items-start gap-2 rounded-2xl border px-3 py-2 text-left text-xs leading-snug transition-colors ${
                            picked
                              ? "border-hotpink bg-hotpink/5 text-ink"
                              : "border-ink/15 bg-white text-ink/70 hover:border-hotpink/50"
                          }`}
                        >
                          <span
                            className={`mt-0.5 grid h-4 w-4 flex-none place-items-center rounded-full border ${
                              picked ? "border-hotpink bg-hotpink text-white" : "border-ink/20 text-transparent"
                            }`}
                          >
                            <Check size={10} />
                          </span>
                          <span>{t}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                <label
                  htmlFor="rule-public-text"
                  className="mb-1 mt-3 block text-xs font-bold uppercase tracking-[0.15em] text-ink/60"
                >
                  Edit or add your own <span className="text-ink/40">(one reply per line)</span>
                </label>
                <textarea
                  id="rule-public-text"
                  value={form.public_reply_text}
                  onChange={(e) => setForm({ ...form, public_reply_text: e.target.value })}
                  placeholder={"Tap replies above to add them here, or type your own, one per line.\nExample: Hey {username}! 👋 Sent you a DM, check your inbox 📩"}
                  rows={4}
                  data-testid="rule-public-text"
                  className="w-full rounded-2xl border border-ink/15 px-3 py-2 focus:border-hotpink focus:outline-none"
                />
                <p className="mt-1 text-xs text-ink/40">
                  We pick one at random for each commenter so replies don&apos;t look repetitive. Select none and we&apos;ll
                  rotate through all {PUBLIC_REPLY_SUGGESTIONS.length} ready-made replies. Placeholders:{" "}
                  <code className="rounded bg-ink/5 px-1 py-0.5">{"{username}"}</code>{" "}
                  <code className="rounded bg-ink/5 px-1 py-0.5">{"{product_name}"}</code>{" "}
                  <code className="rounded bg-ink/5 px-1 py-0.5">{"{product_price}"}</code>{" "}
                  <code className="rounded bg-ink/5 px-1 py-0.5">{"{currency}"}</code>. No links in public replies.
                </p>
              </div>
            )}
          </div>
        </div>

        {selectedProduct && (
          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-ink/10 bg-ink/[0.02] p-3">
            {selectedProduct.image ? (
              <img src={selectedProduct.image} alt="" className="h-10 w-10 rounded-xl object-cover" />
            ) : null}
            <div className="text-xs text-ink/60">
              <p className="font-semibold text-ink">{selectedProduct.title}</p>
              <p>{formatMoney(selectedProduct.price, merchant?.currency)} · this is the product commenters will be shown</p>
              {selectedProduct.sold_out && (
                <p className="mt-1 font-semibold text-hotpink">
                  Currently sold out — commenters will still get this DM, so pause the rule until it's back in stock.
                </p>
              )}
            </div>
          </div>
        )}

        <div className="mt-5 flex items-center gap-3">
          <button
            type="submit"
            disabled={creating}
            data-testid="rule-create-btn"
            className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-70"
          >
            {creating ? <Loader2 size={14} className="animate-spin" /> : editingId ? <Pencil size={14} /> : <Plus size={14} />}
            {editingId ? "Save changes" : "Create rule"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              disabled={creating}
              className="text-sm font-semibold text-ink/50 hover:text-ink"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Rules list */}
      <div ref={rulesListRef} className="scroll-mt-4 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8" data-testid="automation-rules-list">
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-hotpink" />
          <p className="font-display text-lg font-black tracking-tight">Active rules</p>
        </div>
        {loading ? (
          <p className="mt-6 text-sm text-ink/50">Loading…</p>
        ) : rules.length === 0 ? (
          <p className="mt-6 text-sm text-ink/50">No automation rules yet. Create your first above.</p>
        ) : (
          <ul className="mt-6 space-y-3">
            {rules.map((r) => {
              const isToggling = togglingId === r.id;
              return (
                <li
                  key={r.id}
                  className={`flex flex-col gap-3 rounded-2xl border p-4 md:flex-row md:items-center md:justify-between ${
                    editingId === r.id ? "border-hotpink/40 bg-hotpink/[0.03]" : "border-ink/10"
                  }`}
                  data-testid={`rule-row-${r.id}`}
                >
                  <div className="flex flex-1 items-start gap-3">
                    {r.scope === "specific" && (
                      <span
                        className="relative h-12 w-12 flex-none overflow-hidden rounded-xl border border-ink/10 bg-ink/5"
                        title={r.media_id ? `Media ${r.media_id}` : undefined}
                      >
                        {r.media_thumbnail ? (
                          <SafeThumb src={r.media_thumbnail} />
                        ) : (
                          <span className="grid h-full w-full place-items-center text-ink/30">
                            <Film size={16} />
                          </span>
                        )}
                      </span>
                    )}
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-display text-base font-black">
                          {r.product_name || `Product #${r.product_id}`}
                        </p>
                        {r.active ? (
                          <span className="rounded-full bg-mint px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-ink">Active</span>
                        ) : (
                          <span className="rounded-full bg-ink/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-ink/60">Paused</span>
                        )}
                        <span className="rounded-full bg-lilac px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-ink">
                          {r.scope === "specific" ? "One post" : "All posts"}
                        </span>
                        {r.require_follow && (
                          <span className="rounded-full bg-ink px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-cream">
                            Follow required
                          </span>
                        )}
                        <span className="rounded-full bg-ink/5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-ink/60">
                          {r.public_reply_enabled === false ? "Public reply off" : "Public reply on"}
                        </span>
                        {r.product_sold_out && (
                          <span className="rounded-full bg-hotpink/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-hotpink">
                            Sold out
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-ink/60">
                        {r.match_type === "any" ? (
                          "Triggers on any comment"
                        ) : (
                          <>
                            Triggers on:{" "}
                            {(r.keywords || []).map((k) => (
                              <code key={k} className="mx-0.5 rounded bg-ink/5 px-1.5 py-0.5">{k}</code>
                            ))}
                          </>
                        )}
                      </p>
                      <p className="mt-1.5 text-xs text-ink/50">
                        {r.dm_sent} sent
                        {r.dm_failed > 0 && ` · ${r.dm_failed} failed`}
                        {r.dm_last_sent_at && ` · last ${new Date(r.dm_last_sent_at).toLocaleDateString()}`}
                      </p>
                      {r.message_template && (
                        <p className="mt-2 text-sm text-ink/70">
                          <span className="text-ink/40">Reply → </span>{r.message_template}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(r)}
                      disabled={isToggling}
                      data-testid={`rule-edit-${r.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-ink/5 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      onClick={() => toggle(r)}
                      disabled={isToggling}
                      data-testid={`rule-toggle-${r.id}`}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                        isToggling
                          ? "cursor-not-allowed border-ink/10 bg-ink/5 text-ink/40"
                          : "border-ink/15 bg-white hover:bg-ink/5"
                      }`}
                    >
                      {isToggling ? <Loader2 size={12} className="animate-spin" /> : <Power size={12} />}
                      {isToggling ? (r.active ? "Pausing…" : "Activating…") : r.active ? "Pause" : "Activate"}
                    </button>
                    <button
                      onClick={() => remove(r)}
                      disabled={isToggling}
                      data-testid={`rule-delete-${r.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-hotpink/30 bg-white px-3 py-1.5 text-xs font-semibold text-hotpink hover:bg-hotpink/5 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center">
            <p className="font-display text-xl font-black tracking-tight">Delete this automation rule?</p>
            <p className="mt-2 text-sm text-ink/60">
              This stops the rule from triggering on new comments. This can't be undone.
            </p>
            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 rounded-full border border-ink/15 py-2.5 text-sm font-semibold disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 rounded-full bg-hotpink py-2.5 text-sm font-semibold text-white disabled:opacity-60"
              >
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
